import Raven from 'raven-js'
import { Observable } from 'rxjs/Observable'
import { combineEpics } from 'redux-observable'
import { setAccessToken } from '@driftt/web-auth'

import config from 'config'
import { Flow } from 'constants/flowConstants'
import { useNetsuite } from '../billing/selectors'
import * as actions from './actions'
import * as selectors from './selectors'
import * as ProfileActions from 'modules/profile/actions'
import * as OrgActions from 'modules/org/actions'
import * as OrgSelectors from 'modules/org/selectors'
import * as MeetingsActions from 'modules/meetings/actions'
import * as MetricsActions from 'modules/metrics/actions'
import { api } from './api'

import { identifyUser } from 'utils/trackingUtils'
import { countAppMetric } from 'utils/metrics'

const fetchClearbitForEmail = action$ => {
  return action$
    .ofType(actions.fetchClearbitForEmail.toString())
    .pluck('payload')
    .mergeMap(email => {
      return api.fetchClearbitForEmail(email)
        .map(actions.fetchClearbitForEmailSuccess)
        .startWith(actions.fetchClearbitForEmailPending({ email }))
        .catch(error => {
          return Observable.of(actions.fetchClearbitForEmailFailed({ email, error }))
        })
    })
}

const checkUniqueEmail = action$ => {
  return action$
    .ofType(actions.checkUniqueEmail.toString())
    .debounceTime(200)
    .pluck('payload')
    .mergeMap(email => {
      return api
        .checkUniqueEmail(email)
        .map(actions.checkUniqueEmailFailed)
        .catch(error => {
          if (error.status === 404) {
            return Observable.of(actions.checkUniqueEmailSuccess())
          }
          return Observable.of(actions.checkUniqueEmailFailed())
        })
    })
}

const updatePassword = (action$, store) => {
  return action$
    .ofType(actions.updatePassword.toString())
    .filter(() => !selectors.isRepInvitedFlow(store.getState()) && !selectors.isCalendarSeatOnboarding(store.getState()))
    .mergeMap(() => {
      const state = store.getState()
      const userId = selectors.getUserId(state)
      const password = selectors.getPassword(state)
      return api
        .updatePassword(userId, password)
        .map(res => {
          setAccessToken({token: res.access_token})
          return actions.updatePasswordSuccess()
        })
        .startWith(actions.updatePasswordPending())
        .catch(() => Observable.of(actions.updatePasswordFailed()))
    })
}

const updateInvitedRepPassword = (action$, store) => {
  return action$
    .ofType(actions.updatePassword.toString())
    .filter(() => selectors.isRepInvitedFlow(store.getState()) || selectors.isCalendarSeatOnboarding(store.getState()))
    .mergeMap(() => {
      const state = store.getState()
      const password = selectors.getPassword(state)
      const token = selectors.getResetToken(state)
      return api
        .acceptInvitation(password, token)
        .map(res => {
          setAccessToken({token: res.access_token})
          return actions.updatePasswordSuccess()
        })
        .startWith(actions.updatePasswordPending())
        .catch((error) => Observable.of(actions.updatePasswordFailed(error)))
    })
}

const redirectToDriftVideo = (action$, store) => {
  return action$
    .ofType(actions.updatePasswordSuccess.toString())
    .filter(() => selectors.getFlow(store.getState()) === Flow.VIDEO)
    .do(() => window.location.replace(config.DRIFT_VIDEO_INVITE_REDIRECT_URL))
    .ignoreElements()
}

const setTimedVideoGateForExistingHyfyUser = (action$, store) => {
  return action$
    .ofType(OrgActions.createOrgSuccess.toString())
    .filter(() => selectors.getFlow(store.getState()) === Flow.HYFY_MIGRATION)
    .mergeMap(() => {
      if(selectors.redeemedRelayStateSuccess(store.getState())) {
        //only set the gate for people who have a valid relaystate (to prevent folks from stealing pro video)
        return api
          .setTimedVideoGate(OrgSelectors.getOrgId(store.getState()))
          .map(actions.setTimedVideoGateSuccess)
          .startWith(actions.setTimedVideoGatePending())
          .catch((error) => {
            Raven.captureException(error, { logger: 'tracking-signup' })
            return Observable.of(actions.setTimedVideoGateFailure())
          })
      } else {
        return Observable.of(actions.fetchEmailFromRelayFailure())
      }
  })
}

const redirectToDriftVideoForExistingHyfyUser = (action$, store) => {
  return action$
    .ofType(actions.setTimedVideoGateSuccess.toString())
    .filter(() => selectors.getFlow(store.getState()) === Flow.HYFY_MIGRATION)
    .do(() => window.location.replace(config.DRIFT_VIDEO_HOMEPAGE))
    .ignoreElements()
}

const fetchEmailFromRelay = action$ => {
  return action$
    .ofType(actions.fetchEmailFromRelay.toString())
    .pluck('payload')
    .mergeMap(relayToken => {
      return api
        .fetchEmailFromRelay(relayToken)
        .map(actions.fetchEmailFromRelaySuccess)
        .startWith(actions.fetchEmailFromRelayPending())
        .catch(() => Observable.of(actions.fetchEmailFromRelayFailure()))
  })
}

const handleHyFyMigrationErrors = action$ => {
  return action$
    .ofType(actions.fetchEmailFromRelayFailure.toString(), actions.setTimedVideoGateFailure.toString())
    .pluck('payload')
    .do(() => window.location.replace("#hyfy-user-migration-failed"))
    .ignoreElements()
}

const fetchUserWithoutName = (action$, store) => {
  return action$
    .ofType(actions.updatePasswordSuccess.toString())
    .filter(() => selectors.isRepInvitedFlow(store.getState()) || selectors.isCalendarSeatOnboarding(store.getState()))
    .mergeMap(() => {
      return api
        .fetchUser()
        .map(res => actions.fetchUserWithoutNameSuccess(res))
        .startWith(actions.fetchUserWithoutNamePending())
        .catch(() => Observable.of(actions.fetchUserWithoutNameFailed()))
    })
}

const fetchUser = (action$, store) => {
  return action$
    .ofType(actions.fetchUser.toString())
    .mergeMap(() => {
      return api
        .fetchUser()
        .map(res => actions.fetchUserSuccess(res))
        .startWith(actions.fetchUserPending())
        .catch((error) => {
          if (selectors.getFlow(store.getState()) === Flow.VIDEO) {
            Raven.captureException(error, { logger: 'tracking-signup' })
          }
          return Observable.of(actions.fetchUserFailed(error))
        })
    })
}

const countFetchUserFailure = (action$, store) => {
  return action$
    .ofType(actions.fetchUserFailed.toString())
    .pluck('payload')
    .filter(() => selectors.getFlow(store.getState()) === Flow.VIDEO)
    .map((err) => {
      Raven.captureException(err, { logger: 'tracking-signup' })

      return MetricsActions.sendDriftAppMetric(countAppMetric('fetchUser.failed'))
    })
}

const updateStagedChangesForInvitedRep = (action$, store) => {
  return action$
    .ofType(actions.fetchUserWithoutNameSuccess.toString())
    .filter(() => selectors.isRepInvitedFlow(store.getState()))
    .flatMap(() => {
      const state = store.getState()
      const file = selectors.getProfilePicture(state)
      return [
        actions.updateUser(state.user),
        ProfileActions.createProfile(),
        ProfileActions.uploadCoverPhoto(file)
      ]
    })
}

const identifyDriftUserForInvitedRep = (action$, store) => {
  return action$
    .ofType(OrgActions.fetchOrgSuccess.toString())
    .flatMap(() => {
      identifyUser()
      return Observable.empty()
    })
}

const updateUser = (action$, store) => {
  return action$
    .ofType(actions.updateUser.toString())
    .pluck('payload')
    .mergeMap(properties => {
      const state = store.getState()
      const userId = selectors.getUserId(state)
      return api
        .updateUser(userId, properties)
        .map(actions.updateUserSuccess)
        .startWith(actions.updateUserPending())
        .catch(() => Observable.of(actions.updateUserFailed()))
    })
}

const updateProfileWithPicture = (action$, store) => {
  return action$
    .ofType(ProfileActions.uploadCoverPhotoSuccess.toString())
    .pluck('payload')
    .mergeMap(url => {
      const state = store.getState()
      const userId = selectors.getUserId(state)
      return api
        .updateUser(userId, { avatarUrl: url })
        .map(() => actions.updateUserAvatarSuccess())
        .startWith(actions.updateUserAvatarPending())
        .catch((err) => Observable.of(actions.updateUserAvatarFailed()))
    })
}

const updateProfileWithCalendarSetting = (action$, store) => {
  return action$
    .ofType(
      MeetingsActions.updateMicrosoftCalendarSuccess.toString(),
      MeetingsActions.updateGoogleCalendarSuccess.toString()
    )
    .mergeMap(url => {
      const state = store.getState()
      const userId = selectors.getUserId(state)
      return api
        .updateUser(userId, { enableCalendarCta: true })
        .map(() => ProfileActions.updateProfileSuccess())
        .startWith(ProfileActions.updateProfilePending())
        .catch((err) => Observable.of(ProfileActions.updateProfileFailed()))
    })
}

const updateUserProperties = (action$, store) => {
  return action$
    .ofType(actions.updateUserProperties.toString())
    .pluck('payload')
    .mergeMap(({ properties, redirectUrl, userId, retryCount = 0 }) => {
      const state = store.getState()
      const userIdForPatch = userId || selectors.getUserId(state)
      const fetchUserStatus = selectors.fetchUserStatus(state)

      // If we have a user ID, proceed as usual
      if (!!userIdForPatch) {
        return api
          .updateUserProperties(userIdForPatch, properties)
          .map(() => actions.updateUserPropertiesSuccess({ redirectUrl }))
          .startWith(actions.updateUserPropertiesPending())
          .catch((err) => {
            return Observable.of(actions.updateUserPropertiesFailed(err))
          })
      } else {
        switch (fetchUserStatus) {
          case 'pending': // If the fetchUser call is pending, retry w/exp backoff until it completes
            return Observable
              .of(actions.updateUserProperties({ properties, redirectUrl, retryCount: retryCount + 1 }))
              .delay((2 ** retryCount) * 100)
          case 'failed': // If it failed, log an error
            return Observable.of(MetricsActions.sendDriftAppMetric(countAppMetric('userPropertiesPatch.userFetchFailed')))
          default: // Otherwise, fetch the user & retry with the user ID
            return Observable
              .forkJoin(api.fetchUser())
              .switchMap(( value ) => {
                var userId = value && value.length > 0 && value[0].id
                return Observable.of(actions.updateUserProperties({ properties, redirectUrl, userId }))
              })
        }
      }
    })
}

const countUpdateUserPropertiesFailure = (action$, store) => {
  return action$
    .ofType(actions.updateUserPropertiesFailed.toString())
    .pluck('payload')
    .filter(() => selectors.getFlow(store.getState()) === Flow.VIDEO)
    .map((err) => {
      Raven.captureException(err, { logger: 'tracking-signup' })

      return MetricsActions.sendDriftAppMetric(countAppMetric('userPropertiesPatch.failed'))
    })
}

const updateUserPropertiesSuccess = (action$) => {
  return action$
    .ofType(actions.updateUserPropertiesSuccess.toString())
    .pluck('payload')
    .filter(({ redirectUrl }) => !!redirectUrl)
    .do(({ redirectUrl }) => window.location.replace(redirectUrl))
    .ignoreElements()
}

const getLoginUrl = (action$, store) => {
  return action$
    .ofType(actions.getLoginUrl.toString())
    .mergeMap(() => {
      const state = store.getState()
      const email = selectors.getEmail(state)
      return api
        .getLoginUrl(email)
        .then((resp) => {
          window.location.replace(resp.url)
        })
        .catch((err) => actions.getLoginUrlFailed(err))
    })
}

const checkEmailIsSSOEnabled = (action$, store) => {
  return action$
    .ofType(actions.checkEmailIsSSOEnabled.toString())
    .mergeMap(() => {
      const state = store.getState()
      const email = selectors.getEmail(state)

      return api
        .checkEmailIsSSOEnabled(email)
        .map((resp) => actions.checkEmailIsSSOEnabledSuccess(resp))
        .catch((error) => {
          if (selectors.getFlow(store.getState()) === Flow.VIDEO) {
            Raven.captureException(error, { logger: 'tracking-signup' })
          }
          return Observable.of(actions.checkEmailIsSSOEnabledFailed())
        })
        .startWith(actions.checkEmailIsSSOEnabledPending())
    })
}

const checkEmailIsSSOEnabledSuccess = (action$, store) => {
  return action$
    .ofType(actions.checkEmailIsSSOEnabledSuccess.toString())
    .mergeMap(() => {
      const state = store.getState()
      const email = selectors.getEmail(state)
      const password = selectors.getPassword(state)
      const name = selectors.getName(state)
      const emailIsSSOEnabled = selectors.emailIsSSOEnabled(state)
      if(!emailIsSSOEnabled) {
        return Observable.of(OrgActions.createOrg({
          name,
          email,
          password,
          anonymousId: window.analytics.user ? window.analytics.user().anonymousId() : undefined,
          enableDefaultWelcomeMessages: true,
          useNetsuite: useNetsuite(state)
        }))
      } else {
        return Observable.of(actions.getLoginUrl())
      }
    })
}

const findAssociatedDomain = (action$, store) => {
  return action$
    .ofType(actions.findAssociatedDomain.toString())
    .pluck('payload')
    .filter(() => selectors.getFlow(store.getState()) === Flow.CHAT)
    .debounceTime(500)
    .switchMap(() => {
      const state = store.getState()
      const email = selectors.getEmail(state)
      return api
        .findAssociatedDomain(email)
        .map((response) => actions.setAssociatedDomainFound(true))
        .catch((err) => Observable.of(actions.setAssociatedDomainFound(false)))
    })
}

const sendFindMyTeamVerificationEmail = (action$, store) => {
  return action$
    .ofType(actions.sendFindMyTeamVerificationEmail.toString())
    .mergeMap(() => {
      const state = store.getState()
      const email = selectors.getEmail(state)
      return api
        .sendFindMyTeamVerificationEmail(email)
        .map(() => actions.sendFindMyTeamVerificationEmailSuccess(true))
        .catch(() => Observable.of(actions.sendFindMyTeamVerificationEmailFailure()))
    })
}

export const epic = combineEpics(
  checkUniqueEmail,
  updatePassword,
  updateUser,
  updateProfileWithPicture,
  updateInvitedRepPassword,
  identifyDriftUserForInvitedRep,
  fetchUserWithoutName,
  fetchUser,
  updateStagedChangesForInvitedRep,
  updateProfileWithCalendarSetting,
  updateUserProperties,
  getLoginUrl,
  checkEmailIsSSOEnabled,
  checkEmailIsSSOEnabledSuccess,
  fetchClearbitForEmail,
  findAssociatedDomain,
  sendFindMyTeamVerificationEmail,
  redirectToDriftVideo,
  updateUserPropertiesSuccess,
  fetchEmailFromRelay,
  setTimedVideoGateForExistingHyfyUser,
  redirectToDriftVideoForExistingHyfyUser,
  handleHyFyMigrationErrors,
  countUpdateUserPropertiesFailure,
  countFetchUserFailure,
)
